/**
 * HTTP Request Utilities with Rate Limit (429) Handling
 * 
 * This module provides utilities for making HTTP requests with:
 * - Automatic retry with exponential backoff for 429 errors
 * - Configurable retry limits and delays
 * - User-friendly error messages
 */

export interface RateLimitError extends Error {
    status: 429;
    retryAfter?: number;
    resetTime?: number;
}

export interface RetryConfig {
    maxRetries: number;
    initialDelayMs: number;
    maxDelayMs: number;
    backoffMultiplier: number;
    timeoutMs: number; // Request timeout
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
    maxRetries: 3,
    initialDelayMs: 1000,
    maxDelayMs: 30000,
    backoffMultiplier: 2,
    timeoutMs: 15000, // 15 second timeout per request
};

/**
 * Check if an error is a rate limit (429) error
 */
export function isRateLimitError(error: any): error is RateLimitError {
    return error?.status === 429 || error?.message?.includes('429') || error?.message?.includes('Rate limit');
}

/**
 * Calculate delay for next retry using exponential backoff
 */
function calculateRetryDelay(
    attempt: number,
    config: RetryConfig,
    serverRetryAfter?: number
): number {
    // If server provides retry-after header, use it (convert to ms if needed)
    if (serverRetryAfter) {
        // retryAfter could be in seconds or milliseconds depending on API
        const retryAfterMs = serverRetryAfter > 10000 ? serverRetryAfter : serverRetryAfter * 1000;
        return Math.min(retryAfterMs, config.maxDelayMs);
    }

    // Otherwise use exponential backoff
    const delay = config.initialDelayMs * Math.pow(config.backoffMultiplier, attempt);
    return Math.min(delay, config.maxDelayMs);
}

/**
 * Sleep for a specified duration
 */
function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Make an HTTP request with automatic retry for rate limit errors
 * 
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @param retryConfig - Configuration for retry behavior
 * @returns The fetch Response
 */
/**
 * Create an AbortController with timeout
 */
function createTimeoutController(timeoutMs: number): { controller: AbortController; timeoutId: ReturnType<typeof setTimeout> } {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
        controller.abort();
    }, timeoutMs);
    return { controller, timeoutId };
}

export async function fetchWithRetry(
    url: string,
    options: RequestInit = {},
    retryConfig: Partial<RetryConfig> = {}
): Promise<Response> {
    const config = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
        const { controller, timeoutId } = createTimeoutController(config.timeoutMs);

        try {
            const response = await fetch(url, {
                ...options,
                signal: options.signal || controller.signal,
            });

            clearTimeout(timeoutId);

            // If not rate limited, return response (even for other error codes)
            if (response.status !== 429) {
                return response;
            }

            // Rate limit hit - extract retry info
            let retryAfter: number | undefined;

            // Try to get retry-after from response body
            try {
                const errorBody = await response.clone().json();
                retryAfter = errorBody.retryAfter;
                console.warn(`⚠️ Rate limit hit (attempt ${attempt + 1}/${config.maxRetries + 1})`, {
                    url: url.substring(0, 100),
                    retryAfter: errorBody.retryAfter,
                    resetTime: errorBody.resetTime,
                    message: errorBody.message
                });
            } catch {
                // Try to get from header
                const retryAfterHeader = response.headers.get('Retry-After');
                if (retryAfterHeader) {
                    retryAfter = parseInt(retryAfterHeader, 10) * 1000; // Convert seconds to ms
                }
                console.warn(`⚠️ Rate limit hit (attempt ${attempt + 1}/${config.maxRetries + 1})`, {
                    url: url.substring(0, 100),
                    retryAfterHeader
                });
            }

            // If this was the last attempt, throw
            if (attempt === config.maxRetries) {
                const error = new Error(`Rate limit exceeded after ${config.maxRetries + 1} attempts`) as RateLimitError;
                error.status = 429;
                error.retryAfter = retryAfter;
                throw error;
            }

            // Calculate delay and wait
            const delay = calculateRetryDelay(attempt, config, retryAfter);
            console.log(`⏳ Waiting ${Math.round(delay / 1000)}s before retry...`);
            await sleep(delay);

        } catch (error) {
            clearTimeout(timeoutId);

            // Handle timeout/abort errors
            if (error instanceof Error && error.name === 'AbortError') {
                console.warn(`⏱️ Request timeout (attempt ${attempt + 1}/${config.maxRetries + 1}):`, url.substring(0, 80));
                lastError = new Error(`Request timeout after ${config.timeoutMs}ms`);

                // Retry on timeout
                if (attempt < config.maxRetries) {
                    const delay = calculateRetryDelay(attempt, config);
                    console.log(`⏳ Timeout, waiting ${Math.round(delay / 1000)}s before retry...`);
                    await sleep(delay);
                    continue;
                }
                throw lastError;
            }

            // Network error or other fetch failure
            lastError = error instanceof Error ? error : new Error(String(error));

            // Don't retry network errors, only rate limits
            if (!isRateLimitError(error)) {
                throw error;
            }

            // If this was the last attempt, throw
            if (attempt === config.maxRetries) {
                throw lastError;
            }

            const delay = calculateRetryDelay(attempt, config);
            console.log(`⏳ Network error, waiting ${Math.round(delay / 1000)}s before retry...`);
            await sleep(delay);
        }
    }

    throw lastError || new Error('Max retries exceeded');
}

/**
 * Parse rate limit error and return user-friendly message
 */
export function getRateLimitMessage(error: any): string {
    if (!isRateLimitError(error)) {
        return error?.message || 'An error occurred';
    }

    const retryAfter = error.retryAfter;
    if (retryAfter) {
        const minutes = Math.ceil(retryAfter / 60000);
        if (minutes > 1) {
            return `Too many requests. Please try again in ${minutes} minutes.`;
        }
        const seconds = Math.ceil(retryAfter / 1000);
        return `Too many requests. Please try again in ${seconds} seconds.`;
    }

    return 'Too many requests. Please try again later.';
}

/**
 * Hook-friendly rate limit state
 */
export interface RateLimitState {
    isRateLimited: boolean;
    retryAfterMs: number | null;
    message: string | null;
}

/**
 * Create a rate limit state from an error
 */
export function createRateLimitState(error: any): RateLimitState {
    if (!isRateLimitError(error)) {
        return {
            isRateLimited: false,
            retryAfterMs: null,
            message: null
        };
    }

    return {
        isRateLimited: true,
        retryAfterMs: error.retryAfter || null,
        message: getRateLimitMessage(error)
    };
}

/**
 * Clear rate limit state
 */
export const CLEAR_RATE_LIMIT_STATE: RateLimitState = {
    isRateLimited: false,
    retryAfterMs: null,
    message: null
};
